/**
 * KCB 전화번호 본인인증을 처리하는 컴포저블
 * 팝업 기반 인증 플로우를 관리합니다.
 */

import type {
  KcbPhoneVerifyStartResponse,
  KcbPhoneVerifyResultResponse,
  KcbPhoneVerifyResultRequestMemberType,
} from '@template/api';
import { kcbPhoneVerificationService } from '@/services/api';
import { ref, onUnmounted } from 'vue';

export interface UsePhoneVerificationOptions {
  /**
   * 인증 성공 시 호출되는 콜백 함수
   * @param result - 인증 결과 데이터 (KcbPhoneVerifyResultResponse의 모든 필드 포함)
   */
  onSuccess?: (result: KcbPhoneVerifyResultResponse) => void | Promise<void>;
  /**
   * 인증 실패 시 호출되는 콜백 함수
   * @param error - 에러 객체
   */
  onError?: (error: Error) => void;
  /**
   * 인증 팝업을 완료하지 않고 닫았을 때 호출되는 콜백 함수
   */
  onPopupClose?: () => void;
}

export function usePhoneVerification(options?: UsePhoneVerificationOptions) {
  const verifyWindow = ref<Window | null>(null);
  const checkInterval = ref<ReturnType<typeof setInterval> | null>(null);
  const messageHandler = ref<((event: MessageEvent) => void) | null>(null);

  /**
   * KCB 전화번호 인증 시작
   * 팝업을 열고 인증 프로세스를 시작합니다.
   * @param requestCauseCode - 인증요청 사유코드, 회원가입=00, 정보수정=02
   * @param memberType - 회원 구분(INDIVIDUAL, CORPORATE)
   * @param returnMsg - 리턴 메시지 (선택)
   */
  const verifyPhone = async (
    requestCauseCode: string = '00',
    memberType: KcbPhoneVerifyResultRequestMemberType = 'INDIVIDUAL',
    returnMsg: string = 'key1=value1'
  ) => {
    try {
      const response = await kcbPhoneVerificationService.startVerification({
        requestCauseCode,
        returnMsg,
      });

      const { resultCode, resultMessage, moduleToken, cpCode, popupUrl } =
        response.data as KcbPhoneVerifyStartResponse;

      if (resultCode !== 'B000') {
        throw new Error(`KCB 오류: ${resultCode} - ${resultMessage}`);
      }

      const popupWidth = 430;
      const popupHeight = 640;
      const left = (screen.width - popupWidth) / 2;
      const top = (screen.height - popupHeight) / 2;

      verifyWindow.value = window.open(
        '',
        'auth_popup',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=no`
      );

      if (!verifyWindow.value) {
        alert('팝업 차단이 감지되었습니다. 팝업 차단을 해제해주세요.');
        return;
      }

      const form = verifyWindow.value.document.createElement('form');
      form.method = 'post';
      form.action = popupUrl;
      form.name = 'form1';

      const tcInput = verifyWindow.value.document.createElement('input');
      tcInput.type = 'hidden';
      tcInput.name = 'tc';
      tcInput.value = 'kcb.oknm.online.safehscert.popup.cmd.P931_CertChoiceCmd';
      form.appendChild(tcInput);

      const cpCdInput = verifyWindow.value.document.createElement('input');
      cpCdInput.type = 'hidden';
      cpCdInput.name = 'cp_cd';
      cpCdInput.value = cpCode;
      form.appendChild(cpCdInput);

      const mdlTknInput = verifyWindow.value.document.createElement('input');
      mdlTknInput.type = 'hidden';
      mdlTknInput.name = 'mdl_tkn';
      mdlTknInput.value = moduleToken;
      form.appendChild(mdlTknInput);

      const targetIdInput = verifyWindow.value.document.createElement('input');
      targetIdInput.type = 'hidden';
      targetIdInput.name = 'target_id';
      targetIdInput.value = '';
      form.appendChild(targetIdInput);

      verifyWindow.value.document.body.appendChild(form);

      form.submit();

      checkInterval.value = setInterval(() => {
        // 팝업 닫힘 감지 (인증 완료 없이 닫은 경우)
        if (verifyWindow.value && verifyWindow.value.closed) {
          clearInterval(checkInterval.value as ReturnType<typeof setInterval>);
          checkInterval.value = null;
          verifyWindow.value = null;
          if (messageHandler.value) {
            window.removeEventListener('message', messageHandler.value);
            messageHandler.value = null;
          }
          options?.onPopupClose?.();
        }
      }, 500);

      // postMessage를 통한 callback 수신
      // 이전 핸들러가 있으면 먼저 제거
      if (messageHandler.value) {
        window.removeEventListener('message', messageHandler.value);
        messageHandler.value = null;
      }

      messageHandler.value = (event: MessageEvent) => {
        if (event.data && event.data.type === 'kcb_callback' && event.data.token) {
          window.removeEventListener('message', messageHandler.value!);
          messageHandler.value = null;
          if (checkInterval.value) {
            clearInterval(checkInterval.value);
            checkInterval.value = null;
          }
          getVerificationResult(event.data.token, requestCauseCode, memberType);
        }
      };
      window.addEventListener('message', messageHandler.value);
    } catch (error) {
      console.error('KCB 오류:', error);
    }
  };

  /**
   * 인증 결과 조회
   * @param token - 인증 토큰
   * @param requestCauseCode - 인증요청 사유코드
   * @param memberType - 회원 구분(INDIVIDUAL, CORPORATE)
   */
  const getVerificationResult = async (
    token: string,
    requestCauseCode: string,
    memberType: KcbPhoneVerifyResultRequestMemberType
  ) => {
    try {
      const result = await kcbPhoneVerificationService.getVerificationResult({
        moduleToken: token,
        requestCauseCode: requestCauseCode,
        memberType: memberType,
      });

      const verificationResult = result.data as KcbPhoneVerifyResultResponse;

      // 콜백 함수가 있으면 호출
      if (options?.onSuccess) {
        await options.onSuccess(verificationResult);
      }
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error('인증 결과 조회 중 오류가 발생했습니다.');
      console.error('KCB 오류:', errorObj);

      // 에러 콜백 함수가 있으면 호출
      if (options?.onError) {
        options.onError(errorObj);
      }
    }
  };

  /**
   * 리소스 정리
   */
  const cleanup = () => {
    if (checkInterval.value) {
      clearInterval(checkInterval.value);
      checkInterval.value = null;
    }
    if (messageHandler.value) {
      window.removeEventListener('message', messageHandler.value);
      messageHandler.value = null;
    }
    if (verifyWindow.value && !verifyWindow.value.closed) {
      verifyWindow.value.close();
      verifyWindow.value = null;
    }
  };

  // 컴포넌트 언마운트 시 정리
  onUnmounted(() => {
    cleanup();
  });

  return {
    verifyPhone,
    cleanup,
  };
}
